import { Role } from '@/enum/global.enum'

/**
 * Role checking utility functions for determining user permissions and access levels.
 *
 * This module provides helper functions to check if a given role belongs to specific
 * role categories (admin roles vs organisation roles). These functions are commonly
 * used in components that need to conditionally render UI elements based on user roles.
 */

/**
 * Checks if the given role is an admin role.
 *
 * Admin roles have elevated privileges and can access system-wide administrative
 * features including user management, system configuration, and global settings.
 *
 * @param role - The role string to check
 * @returns `true` if the role is SUPER_ADMIN or ADMIN, `false` otherwise
 *
 * @example
 * ```typescript
 * isAdminRole('SUPER_ADMIN') // returns true
 * isAdminRole('ADMIN')       // returns true
 * isAdminRole('ORG_ADMIN')   // returns false
 * isAdminRole('USER')        // returns false
 * ```
 */
export const isAdminRole = (role: string): boolean => role === Role.SUPER_ADMIN || role === Role.ADMIN

/**
 * Checks if the given role is an organisation role.
 *
 * Organisation roles have access to organisation-specific features and data.
 * These roles are typically assigned to users who work within a specific
 * organisation and need access to governance, risk, and compliance features.
 *
 * @param role - The role string to check
 * @returns `true` if the role is any organisation role, `false` otherwise
 *
 * @example
 * ```typescript
 * isOrgRole('ORG_ADMIN')     // returns true
 * isOrgRole('ORG_MANAGER')   // returns true
 * isOrgRole('ORG_CONSULTANT') // returns true
 * isOrgRole('SUPER_ADMIN')   // returns false
 * isOrgRole('ADMIN')         // returns false
 * ```
 *
 * @remarks
 * Organisation roles include:
 * - ORG_ADMIN: Organisation administrator with full access to org features
 * - ORG_MANAGER: Organisation manager with management-level access
 * - ORG_CONSULTANT: External consultant with limited access
 * - ORG_ASSESSOR: Assessment specialist with assessment-related access
 * - ORG_AUDITOR: Auditor with audit-related access
 * - ORG_VIEWER: Read-only access to organisation data
 */
/**
 * Checks if the given role is a vendor (supply chain) role.
 *
 * Vendor roles belong to external suppliers who access the platform to
 * respond to assessments assigned to them. They are not org-staff and only see
 * the vendor portal surface.
 *
 * @param role - The role string to check
 * @returns `true` if the role is VENDOR or VENDOR_COLLABORATOR, `false` otherwise
 */
export const isVendorRole = (role: string): boolean => role === Role.VENDOR || role === Role.VENDOR_COLLABORATOR

/**
 * CSP annual reporting calendar: only the Agency Head (ORG_ADMIN, decision D2)
 * may mark a reporting deadline submitted. Deadline dates themselves are never editable by any
 * role. (There is no CISO role in practice.)
 *
 * @param role - The role string to check
 * @returns `true` if the role is ORG_ADMIN, `false` otherwise
 */
export const canMarkCspReportingDeadlineSubmitted = (role: string): boolean => role === Role.ORG_ADMIN

/**
 * F04 feature-completeness item: only the Agency Head (ORG_ADMIN, no CISO role in practice) may
 * request changes / return a completed CSP review for re-review.
 *
 * @param role - The role string to check
 * @returns `true` if the role is ORG_ADMIN, `false` otherwise
 */
export const canRequestCspAttestationChanges = (role: string): boolean => role === Role.ORG_ADMIN

/**
 * risk manager team may edit the risk
 * appetite/tolerance draft and submit it for endorsement.
 *
 * @param role - The role string to check
 * @returns `true` if the role is ORG_RISK_MANAGER or ORG_ADMIN, `false` otherwise
 */
export const canEditRiskAppetite = (role: string): boolean => role === Role.ORG_RISK_MANAGER || role === Role.ORG_ADMIN

/**
 * only the Agency Head (ORG_ADMIN, no CISO role in
 * practice) may endorse a submitted risk appetite/tolerance configuration.
 *
 * @param role - The role string to check
 * @returns `true` if the role is ORG_ADMIN, `false` otherwise
 */
export const canEndorseRiskAppetite = (role: string): boolean => role === Role.ORG_ADMIN

/**
 * only ORG_ADMIN / ORG_RISK_MANAGER may
 * generate the High/Extreme Residual Risk Report (mirrors the backend route's `generateRoles`).
 *
 * @param role - The role string to check
 * @returns `true` if the role is ORG_ADMIN or ORG_RISK_MANAGER, `false` otherwise
 */
export const canGenerateHighExtremeResidualRiskReport = (role: string): boolean =>
  role === Role.ORG_ADMIN || role === Role.ORG_RISK_MANAGER

/**
 * NSW CSP exception requests (D3): approve/reject are CISO-semantic decisions - restricted to the
 * Agency Head (ORG_ADMIN, no CISO role in practice). Mirrors the backend route's `decisionRoles`.
 * Raising/editing a request itself uses the broader assessment write-role set, unrestricted here.
 *
 * @param role - The role string to check
 * @returns `true` if the role is ORG_ADMIN, `false` otherwise
 */
export const canDecideCspExceptionRequest = (role: string): boolean => role === Role.ORG_ADMIN

export const isOrgRole = (role: string): boolean =>
  role === Role.ORG_ADMIN ||
  role === Role.ORG_MANAGER ||
  role === Role.ORG_ASSESSOR ||
  role === Role.ORG_VIEWER ||
  role === Role.ORG_CONSULTANT ||
  role === Role.ORG_AUDITOR ||
  role === Role.ORG_CISO ||
  role === Role.ORG_COMPLIANCE_MANAGER ||
  role === Role.ORG_RISK_MANAGER ||
  role === Role.ORG_POLICY_MANAGER ||
  role === Role.ORG_INTERNAL_AUDITOR ||
  role === Role.ORG_EXTERNAL_AUDITOR ||
  role === Role.ORG_LEAD_QSA ||
  role === Role.ORG_QSA_SUBCONTRACTOR ||
  role === Role.ORG_USER
